import axios from "../axios/axios";
import {
  AssociationCategoryResponse,
  AssociationRequest,
  AssociationResponse,
  AssociationWorkingHoursEntry,
} from "../types/association.types";
import { AdvisorProfileResponse } from "../types/advisorProfile.types";
import {
  AssociationAdvisorsPage,
  AssociationAdvisorsParams,
} from "../types/associationRoster.types";

// Association portal service. Every endpoint here is open to ASSOCIATION_USER;
// the writes re-check the administrator pairing server-side and answer
// 400 `ASSOCIATION_NOT_ADMINISTERED` (never 403) on a mismatch, so this
// branch's shared axios instance is safe to use throughout.
//
// Slice 1 ships the reads. The writes (update, working hours, delete, roster)
// are added by their slices, additively, below this comment block.

const ENDPOINT = "/association";

/**
 * GET /association/mine: the administered, non-deleted associations,
 * unpublished ones included. An empty list is a valid answer ("the account
 * administers nothing"), never an error.
 */
export const getMyAssociations = (
  signal?: AbortSignal,
): Promise<AssociationResponse[]> =>
  axios
    .get<AssociationResponse[]>(`${ENDPOINT}/mine`, { signal })
    .then((res) => res.data);

/** GET /association/{id}: same shape as a `/mine` row, any signed-in role. */
export const getAssociation = (
  id: number,
  signal?: AbortSignal,
): Promise<AssociationResponse> =>
  axios
    .get<AssociationResponse>(`${ENDPOINT}/${id}`, { signal })
    .then((res) => res.data);

/** GET /association/category: ordered by `ordinal`, counts omitted. */
export const getAssociationCategories = (
  signal?: AbortSignal,
): Promise<AssociationCategoryResponse[]> =>
  axios
    .get<AssociationCategoryResponse[]>(`${ENDPOINT}/category`, {
      params: { withCounts: false },
      signal,
    })
    .then((res) => res.data);

// ---- Slice 5 ----
// The advisor roster. The read is open to any signed-in role; add and remove
// re-check the administrator pairing (400 `ASSOCIATION_NOT_ADMINISTERED`).

/**
 * GET /association/{id}/advisor: one page of the association's advisors.
 * Undefined params are dropped by axios, so the server defaults apply.
 */
export const getAssociationAdvisors = (
  id: number,
  params: AssociationAdvisorsParams,
  signal?: AbortSignal,
): Promise<AssociationAdvisorsPage> =>
  axios
    .get<AssociationAdvisorsPage>(`${ENDPOINT}/${id}/advisor`, {
      params,
      signal,
    })
    .then((res) => res.data);

/**
 * POST /association/{id}/advisor `{email}`: adds an existing advisor (by the
 * e-mail they registered with). 201 with the advisor's profile.
 */
export const addAdvisorToAssociation = (
  id: number,
  email: string,
): Promise<AdvisorProfileResponse> =>
  axios
    .post<AdvisorProfileResponse>(`${ENDPOINT}/${id}/advisor`, { email })
    .then((res) => res.data);

/**
 * DELETE /association/{id}/advisor/{advisorProfileId}: removes the advisor
 * from this association only; the profile and its other memberships stay.
 */
export const removeAdvisorFromAssociation = (
  id: number,
  advisorProfileId: number,
): Promise<void> =>
  axios
    .delete(`${ENDPOINT}/${id}/advisor/${advisorProfileId}`)
    .then(() => undefined);

// ---- Slice 4 ----

/**
 * PUT /association/{id} (multipart): part `associationRequestString` holds the
 * JSON request, every uploaded file goes in an `images` part. Overwrites the
 * scalars unconditionally, so callers send the complete set. Media is touched
 * only when `updateImages: true`, and then rows pair with files IN ORDER
 * (every row whose `location` matches no stored file consumes the next file).
 * 200 answers the updated association.
 */
export const updateAssociation = async (
  id: number,
  request: AssociationRequest,
  files: File[] = [],
): Promise<AssociationResponse> => {
  const formData = new FormData();
  formData.append("associationRequestString", JSON.stringify(request));
  files.forEach((file) => {
    formData.append("images", file);
  });
  const res = await axios.put<AssociationResponse>(`${ENDPOINT}/${id}`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
};

// ---- Slice 3 ----

/**
 * PUT /association/{id}/working_hours, JSON `[{dayOfWeek, openFrom, openUntil}]`
 * ("HH:mm"): REPLACES the whole schedule, `[]` clears it. 200 with no body,
 * so the caller re-reads `/mine` for server truth. The backend performs no
 * ordering check and silently drops rows without a `dayOfWeek`; the editor's
 * validation is the only guard.
 */
export const updateAssociationWorkingHours = (
  id: number,
  entries: AssociationWorkingHoursEntry[],
): Promise<void> =>
  axios.put(`${ENDPOINT}/${id}/working_hours`, entries).then(() => undefined);

// ---- Slice 2/6 ----
// `updateAssociation` (the multipart PUT the data form and the publication
// toggle use) is defined in the Slice 4 block above with the shape both need.

/**
 * DELETE /association/{id}: soft delete, 200 with no body. The advisors and
 * their profiles stay; the row leaves `/association/mine`. Undo is ADMIN-only.
 */
export const deleteAssociation = (id: number): Promise<void> =>
  axios.delete(`${ENDPOINT}/${id}`).then(() => undefined);
